/* =====================================================================
   Kherish sections — interactions & animation
   - Every section root carries data-kh="component names".
   - Components mount on load and re-mount live in the theme editor
     (shopify:section:load / unload / block:select).
   - GSAP + ScrollTrigger + Lenis are optional: without them the page
     still works (CSS fallbacks).
   ===================================================================== */
(function () {
  'use strict';
  if (window.KH) return;
  var KH = (window.KH = {});
  var doc = document;
  var html = doc.documentElement;
  var CFG = window.KH_CFG || {};
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var design = !!(window.Shopify && window.Shopify.designMode);
  var fine = window.matchMedia('(pointer: fine)').matches;
  var G = function () { return !!(window.gsap && window.ScrollTrigger) && !reduce; };
  var $ = function (s, r) { return (r || doc).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || doc).querySelectorAll(s)); };
  var pad = function (n) { return String(n).padStart(2, '0'); };
  var esc = function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };

  /* ---------------- component registry ---------------- */
  var C = {};
  var live = new Map();

  function mountEl(el) {
    if (live.has(el)) return;
    var list = [];
    live.set(el, list);
    var api = {
      on: function (t, type, fn, opt) { t.addEventListener(type, fn, opt); list.push(function () { t.removeEventListener(type, fn, opt); }); },
      add: function (fn) { list.push(fn); }
    };
    (el.getAttribute('data-kh') || '').split(/\s+/).forEach(function (name) {
      var fn = C[name];
      if (!fn) return;
      try {
        if (G()) { var ctx = gsap.context(function () { fn(el, api); }); list.push(function () { ctx.revert(); }); }
        else fn(el, api);
      } catch (e) { console.error('[Kherish] ' + name, e); }
    });
  }
  function unmountEl(el) {
    var l = live.get(el);
    if (!l) return;
    l.forEach(function (f) { try { f(); } catch (e) {} });
    live.delete(el);
    if (G()) { gsap.killTweensOf(el); gsap.killTweensOf($$('*', el)); }
  }
  function mount(root) {
    if (root.matches && root.matches('[data-kh]')) mountEl(root);
    $$('[data-kh]', root).forEach(mountEl);
    refresh();
  }
  function unmount(root) {
    Array.from(live.keys()).forEach(function (el) { if (root === el || root.contains(el)) unmountEl(el); });
    refresh();
  }
  var rT;
  function refresh() { if (!G()) return; clearTimeout(rT); rT = setTimeout(function () { ScrollTrigger.refresh(); }, 150); }
  KH.refresh = refresh;

  /* ---------------- intro (preloader) ---------------- */
  var introDone;
  KH.intro = new Promise(function (r) { introDone = r; });

  /* ---------------- smooth scroll ---------------- */
  var lenis = null;
  function initLenis() {
    if (lenis || design || reduce || !window.Lenis || !G()) return;
    if (!$('[data-kh-smooth="true"]')) return;
    lenis = new window.Lenis({ duration: 1.15, easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); } });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(function (t) { lenis.raf(t * 1000); });
    gsap.ticker.lagSmoothing(0);
    KH.lenis = lenis;
  }
  KH.scrollTo = function (y) {
    if (lenis) lenis.scrollTo(y, { duration: 1.4 });
    else window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
  };
  function lock(on) {
    html.classList.toggle('kh-lock', on);
    if (lenis) on ? lenis.stop() : lenis.start();
  }

  /* ---------------- scroll velocity bus ---------------- */
  var velSubs = new Set();
  function onVelocity(fn) { velSubs.add(fn); return function () { velSubs.delete(fn); }; }
  function initVelocity() {
    if (!G()) return;
    ScrollTrigger.create({ onUpdate: function (s) { var v = s.getVelocity(), d = s.direction; velSubs.forEach(function (f) { f(v, d); }); } });
  }

  /* ---------------- money ---------------- */
  function money(cents) {
    var f = CFG.moneyFormat || '${{amount}}';
    var m = f.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    var n = (cents || 0) / 100;
    var fix = function (num, dec, th, ds) {
      var p = num.toFixed(dec).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return p.join(ds);
    };
    var v;
    switch (key) {
      case 'amount_no_decimals': v = fix(n, 0, ',', '.'); break;
      case 'amount_with_comma_separator': v = fix(n, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': v = fix(n, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': v = fix(n, 2, "'", '.'); break;
      case 'amount_with_space_separator': v = fix(n, 2, ' ', ','); break;
      default: v = fix(n, 2, ',', '.');
    }
    return f.replace(/\{\{\s*\w+\s*\}\}/, v);
  }
  KH.money = money;

  /* ---------------- cart ---------------- */
  var J = { 'Content-Type': 'application/json', Accept: 'application/json' };
  KH.cart = {
    get: function () { return fetch((CFG.cartUrl || '/cart') + '.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }); },
    add: function (id, qty) {
      return fetch((CFG.cartAddUrl || '/cart/add') + '.js', { method: 'POST', headers: J, body: JSON.stringify({ items: [{ id: +id, quantity: qty || 1 }] }) })
        .then(function (r) { return r.json().then(function (d) { if (!r.ok) throw d; return d; }); });
    },
    change: function (key, qty) {
      return fetch((CFG.cartChangeUrl || '/cart/change') + '.js', { method: 'POST', headers: J, body: JSON.stringify({ id: key, quantity: qty }) })
        .then(function (r) { return r.json(); });
    }
  };
  function imgW(url, w) { if (!url) return ''; return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w; }

  function renderCart(cart) {
    $$('[data-kh-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
    var d = $('#kh-drawer');
    if (!d) return;
    var list = $('[data-kh-cart-items]', d);
    var ds = d.dataset;
    list.innerHTML = cart.items.length ? cart.items.map(function (it) {
      return '<div class="kh-ditem">' +
        '<a class="kh-ditem__th" href="' + esc(it.url) + '">' + (it.image ? '<img src="' + esc(imgW(it.image, 200)) + '" alt="" loading="lazy">' : '') + '</a>' +
        '<div><a class="kh-ditem__title" href="' + esc(it.url) + '">' + esc(it.product_title) + '</a>' +
        (it.variant_title ? '<span class="kh-ditem__variant">' + esc(it.variant_title) + '</span>' : '') +
        '<div class="kh-qty"><button type="button" data-kh-qty="' + esc(it.key) + '" data-q="' + (it.quantity - 1) + '" aria-label="' + esc(ds.decrease) + '">−</button><span>' + it.quantity + '</span>' +
        '<button type="button" data-kh-qty="' + esc(it.key) + '" data-q="' + (it.quantity + 1) + '" aria-label="' + esc(ds.increase) + '">+</button></div></div>' +
        '<div class="kh-ditem__price"><span>' + money(it.final_line_price) + '</span><button type="button" class="kh-ditem__rm" data-kh-qty="' + esc(it.key) + '" data-q="0">' + esc(ds.remove) + '</button></div>' +
        '</div>';
    }).join('') : '<p class="kh-drawer__empty">' + esc(ds.empty) + '</p>';
    var total = $('[data-kh-cart-total]', d);
    if (total) total.innerHTML = money(cart.total_price);
    var ship = $('[data-kh-ship]', d);
    if (ship) {
      var th = +ds.threshold || 0;
      var txt = $('span', ship);
      if (th > 0 && cart.total_price < th) {
        txt.innerHTML = esc(ds.shipLeft || '').replace('[amount]', money(th - cart.total_price));
        ship.style.setProperty('--p', Math.min(1, cart.total_price / th));
      } else {
        txt.textContent = ds.shipDone || '';
        ship.style.setProperty('--p', cart.items.length ? 1 : 0);
      }
    }
  }
  KH.renderCart = function () { return KH.cart.get().then(renderCart); };

  var drawerOpen = function () {
    var d = $('#kh-drawer'), o = $('#kh-overlay');
    if (!d) return false;
    d.classList.add('is-open'); d.setAttribute('aria-hidden', 'false');
    o && o.classList.add('is-open');
    lock(true);
    setTimeout(function () { var c = $('[data-kh-cart-close]', d); c && c.focus(); }, 300);
    return true;
  };
  var drawerClose = function () {
    var d = $('#kh-drawer'), o = $('#kh-overlay');
    if (!d || !d.classList.contains('is-open')) return;
    d.classList.remove('is-open'); d.setAttribute('aria-hidden', 'true');
    o && o.classList.remove('is-open');
    lock(false);
  };
  KH.openCart = function () { return KH.renderCart().then(drawerOpen); };

  function initCart() {
    doc.addEventListener('click', function (e) {
      var add = e.target.closest('[data-kh-add]');
      if (add) {
        e.preventDefault();
        if (add.disabled) return;
        var label = add.textContent;
        add.disabled = true; add.classList.add('is-loading');
        KH.cart.add(add.getAttribute('data-kh-add')).then(function () {
          add.textContent = (add.dataset.added || 'Added') + ' ✓';
          doc.dispatchEvent(new CustomEvent('kh:cart:added'));
          if ($('#kh-drawer')) return KH.openCart();
          window.location.href = CFG.cartUrl || '/cart';
        }).catch(function (err) {
          add.textContent = (err && (err.description || err.message)) || 'Unavailable';
        }).then(function () {
          setTimeout(function () { add.textContent = label; add.disabled = false; add.classList.remove('is-loading'); }, 1800);
        });
        return;
      }
      if (e.target.closest('[data-kh-cart-open]')) { e.preventDefault(); KH.openCart(); return; }
      if (e.target.closest('[data-kh-cart-close]')) { drawerClose(); return; }
      var q = e.target.closest('[data-kh-qty]');
      if (q) {
        var d = $('#kh-drawer');
        d && d.classList.add('is-loading');
        KH.cart.change(q.getAttribute('data-kh-qty'), +q.dataset.q).then(renderCart).then(function () { d && d.classList.remove('is-loading'); });
      }
    });
    doc.addEventListener('keydown', function (e) { if (e.key === 'Escape') drawerClose(); });
  }

  /* ---------------- helpers ---------------- */
  function splitWords(el) {
    if (el.dataset.khSplitDone) return $$('.kh-wi', el);
    el.dataset.khSplitDone = '1';
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var frag = doc.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(doc.createTextNode(' ')); return; }
            var m = doc.createElement('span'); m.className = 'kh-wm';
            var i = doc.createElement('span'); i.className = 'kh-wi'; i.textContent = part;
            m.appendChild(i); frag.appendChild(m);
          });
          n.parentNode.replaceChild(frag, n);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') walk(n);
      });
    })(el);
    return $$('.kh-wi', el);
  }
  function quiet(node) {
    var c = node.cloneNode(true);
    c.setAttribute('aria-hidden', 'true');
    c.setAttribute('data-kh-clone', '');
    [c].concat($$('*', c)).forEach(function (n) {
      n.removeAttribute('data-shopify-editor-block');
      n.removeAttribute('id');
      if (n.matches('a,button,input,select,textarea,[tabindex]')) n.setAttribute('tabindex', '-1');
    });
    return c;
  }

  /* =====================================================================
     COMPONENTS
     ===================================================================== */

  /* ----- fx: generic scroll effects for any section ----- */
  C.fx = function (el, api) {
    var heads = $$('[data-kh-split]', el);
    heads.forEach(function (h) {
      var w = splitWords(h);
      if (G()) gsap.from(w, { yPercent: 115, rotate: 3, duration: 1.3, stagger: 0.06, ease: 'power4.out', scrollTrigger: { trigger: h, start: 'top 90%' } });
    });
    if (!G()) return;
    var fades = $$('[data-kh-fade]', el);
    if (fades.length) {
      gsap.set(fades, { opacity: 0, y: 40 });
      var show = function (b) { gsap.to(b, { opacity: 1, y: 0, duration: 1.1, stagger: 0.08, ease: 'power3.out', overwrite: true }); };
      ScrollTrigger.batch(fades, { start: 'top 92%', onEnter: show, onEnterBack: show });
    }
    $$('[data-kh-stagger]', el).forEach(function (g) {
      gsap.from(g.children, { y: 60, opacity: 0, duration: 1.1, stagger: 0.08, ease: 'power3.out', scrollTrigger: { trigger: g, start: 'top 88%' } });
    });
    /* failsafe: if scroll triggers were measured before the page finished growing, items can stay hidden.
       Anything visible on screen but still transparent after 1.6s is forced in. */
    (function () {
      var targets = [];
      $$('[data-kh-stagger]', el).forEach(function (g) { [].forEach.call(g.children, function (c) { targets.push(c); }); });
      $$('[data-kh-fade]', el).forEach(function (f) { targets.push(f); });
      if (!targets.length) return;
      var tick = function () {
        var vh = window.innerHeight || 800;
        targets.forEach(function (t) {
          var r = t.getBoundingClientRect();
          if (r.bottom < 0 || r.top > vh * 0.98 || !r.width) return;
          if (parseFloat(gsap.getProperty(t, 'opacity')) < 0.05 && !gsap.isTweening(t)) {
            gsap.to(t, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', overwrite: true });
          }
        });
      };
      var iv = setInterval(tick, 700);
      api.on(window, 'scroll', tick);
      setTimeout(function () { ScrollTrigger.refresh(); }, 1200);
      setTimeout(function () { clearInterval(iv); }, 20000);
    })();
    $$('[data-kh-reveal]', el).forEach(function (r) {
      gsap.fromTo(r, { clipPath: 'inset(100% 0% 0% 0% round 12px)' }, { clipPath: 'inset(0% 0% 0% 0% round 12px)', duration: 1.5, ease: 'power4.inOut', scrollTrigger: { trigger: r, start: 'top 88%' } });
      var im = $('img', r);
      if (im) {
        gsap.fromTo(im, { scale: 1.3 }, { scale: 1.08, duration: 2.2, ease: 'power3.out', scrollTrigger: { trigger: r, start: 'top 88%' } });
        gsap.fromTo(im, { yPercent: -4 }, { yPercent: 4, ease: 'none', scrollTrigger: { trigger: r, start: 'top bottom', end: 'bottom top', scrub: true } });
      }
    });
    $$('[data-kh-parallax]', el).forEach(function (p) {
      gsap.fromTo(p, { yPercent: -8 }, { yPercent: 8, ease: 'none', scrollTrigger: { trigger: p.parentElement, start: 'top bottom', end: 'bottom top', scrub: true } });
    });
    $$('[data-kh-clip]', el).forEach(function (c) {
      gsap.fromTo(c, { clipPath: 'inset(8% 5% 8% 5% round 14px)' }, { clipPath: 'inset(0% 0% 0% 0% round 14px)', ease: 'none', scrollTrigger: { trigger: c, start: 'top 95%', end: 'top 45%', scrub: true } });
    });
    $$('[data-kh-count]', el).forEach(function (c) {
      var raw = c.getAttribute('data-kh-count').replace(/,/g, '');
      var end = parseFloat(raw);
      if (isNaN(end)) return;
      var dec = (raw.split('.')[1] || '').length, o = { v: 0 };
      var useComma = /,/.test(c.textContent) || end >= 1000;
      var fmt = function (v) { return useComma ? v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec }) : v.toFixed(dec); };
      c.textContent = fmt(0);
      gsap.to(o, { v: end, duration: 2.4, ease: 'power2.out', scrollTrigger: { trigger: c, start: 'top 92%' }, onUpdate: function () { c.textContent = fmt(o.v); } });
    });
    $$('[data-kh-letters]', el).forEach(function (w) {
      gsap.from(w.children, { yPercent: 100, duration: 1.5, stagger: 0.07, ease: 'power4.out', scrollTrigger: { trigger: w, start: 'top 100%' } });
    });
    if (fine) {
      $$('[data-kh-tilt]', el).forEach(function (t) {
        gsap.set(t, { transformPerspective: 900 });
        var rx = gsap.quickTo(t, 'rotationX', { duration: 0.7, ease: 'power3' }), ry = gsap.quickTo(t, 'rotationY', { duration: 0.7, ease: 'power3' });
        api.on(t, 'mousemove', function (e) { var r = t.getBoundingClientRect(); ry(((e.clientX - r.left) / r.width - 0.5) * 9); rx(-((e.clientY - r.top) / r.height - 0.5) * 9); });
        api.on(t, 'mouseleave', function () { rx(0); ry(0); });
      });
      $$('.kh-magnetic', el).forEach(function (b) {
        var xTo = gsap.quickTo(b, 'x', { duration: 0.6, ease: 'power3' }), yTo = gsap.quickTo(b, 'y', { duration: 0.6, ease: 'power3' });
        api.on(b, 'mousemove', function (e) { var r = b.getBoundingClientRect(); xTo((e.clientX - r.left - r.width / 2) * 0.3); yTo((e.clientY - r.top - r.height / 2) * 0.4); });
        api.on(b, 'mouseleave', function () { xTo(0); yTo(0); });
      });
    }
  };

  /* ----- announcement rotator ----- */
  C.announce = function (el, api) {
    var items = $$('.kh-announce__item', el);
    if (items.length < 2) return;
    var i = 0, ms = Math.max(2, +el.dataset.interval || 4) * 1000, t;
    var show = function (n) { items[i].classList.remove('is-on'); i = (n + items.length) % items.length; items[i].classList.add('is-on'); };
    var start = function () { clearInterval(t); t = setInterval(function () { show(i + 1); }, ms); };
    start();
    api.add(function () { clearInterval(t); });
    api.on(el, 'shopify:block:select', function (e) { clearInterval(t); var n = items.indexOf(e.target); if (n > -1) show(n); });
    api.on(el, 'shopify:block:deselect', start);
  };

  /* ----- header: mega menu, mobile menu, drawer, chrome ----- */
  C.header = function (el, api) {
    var section = el.closest('.shopify-section') || el;
    var portals = $$('[data-kh-portal]', el);
    portals.forEach(function (p) { doc.body.appendChild(p); });
    api.add(function () { portals.forEach(function (p) { p.remove(); }); html.classList.remove('kh-cursor-on'); lock(false); });

    /* mega menu */
    var triggers = $$('[data-kh-mega]', el), panels = $$('[data-kh-mega-panel]', el), t;
    var close = function () { panels.forEach(function (p) { p.classList.remove('is-open'); }); triggers.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); }); };
    triggers.forEach(function (b) {
      var p = panels.filter(function (x) { return x.getAttribute('data-kh-mega-panel') === b.getAttribute('data-kh-mega'); })[0];
      if (!p) return;
      var open = function () { clearTimeout(t); close(); p.classList.add('is-open'); b.setAttribute('aria-expanded', 'true'); };
      api.on(b, 'mouseenter', open);
      api.on(b, 'focus', open);
    });
    $$('.kh-nav__item:not([data-kh-mega])', el).forEach(function (a) { api.on(a, 'mouseenter', close); });
    api.on(el, 'mouseleave', function () { t = setTimeout(close, 200); });
    api.on(el, 'mouseenter', function () { clearTimeout(t); });
    api.on(el, 'focusout', function (e) { if (!el.contains(e.relatedTarget)) close(); });
    api.on(el, 'shopify:block:select', function (e) { var p = e.target.closest('[data-kh-mega-panel]'); if (p) { close(); p.classList.add('is-open'); } });
    api.on(el, 'shopify:block:deselect', close);

    /* auto-fit: if the menu is too long for the layout, move the logo left, then fall back to the burger */
    var nav = $('.kh-nav', el);
    var fit = function () {
      if (!nav) return;
      el.classList.remove('is-auto-left', 'is-compact');
      if (window.innerWidth < 990) return;
      var over = function () { return nav.scrollWidth > nav.clientWidth + 1; };
      if (over() && !el.classList.contains('kh-header--left')) el.classList.add('is-auto-left');
      if (over()) el.classList.add('is-compact');
    };
    fit();
    api.on(window, 'resize', fit);
    api.on(window, 'load', fit);
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(fit);

    /* mobile menu */
    var menu = portals.filter(function (p) { return p.classList.contains('kh-mmenu'); })[0];
    var openMenu = function () { if (!menu) return; menu.classList.add('is-open'); menu.setAttribute('aria-hidden', 'false'); lock(true); var c = $('[data-kh-menu-close]', menu); c && setTimeout(function () { c.focus(); }, 300); };
    var closeMenu = function () { if (!menu || !menu.classList.contains('is-open')) return; menu.classList.remove('is-open'); menu.setAttribute('aria-hidden', 'true'); lock(false); };
    api.on(doc, 'click', function (e) {
      if (e.target.closest('[data-kh-menu-open]')) openMenu();
      else if (e.target.closest('[data-kh-menu-close]') || (menu && e.target.closest('.kh-mmenu a'))) closeMenu();
    });
    api.on(doc, 'keydown', function (e) { if (e.key === 'Escape') { close(); closeMenu(); } });

    if (!G()) return;

    /* hide on scroll down */
    if (el.dataset.hide === 'true' && el.classList.contains('is-sticky')) {
      ScrollTrigger.create({
        start: 'top -300', end: 99999,
        onUpdate: function (s) { if (!$('.kh-mega.is-open', el)) section.classList.toggle('kh-hidden', s.direction === 1); },
        onLeaveBack: function () { section.classList.remove('kh-hidden'); }
      });
      api.add(function () { section.classList.remove('kh-hidden'); });
    }

    /* scroll progress */
    var prog = $('.kh-progress');
    if (prog && portals.indexOf(prog) > -1) gsap.to(prog, { scaleX: 1, ease: 'none', scrollTrigger: { start: 0, end: 'max', scrub: 0.3 } });

    /* custom cursor */
    var dot = portals.filter(function (p) { return p.classList.contains('kh-cursor'); })[0];
    var ring = portals.filter(function (p) { return p.classList.contains('kh-cursor-ring'); })[0];
    if (dot && ring && fine) {
      html.classList.add('kh-cursor-on');
      gsap.set([dot, ring], { xPercent: -50, yPercent: -50 });
      var dx = gsap.quickTo(dot, 'x', { duration: 0.1 }), dy = gsap.quickTo(dot, 'y', { duration: 0.1 });
      var rx = gsap.quickTo(ring, 'x', { duration: 0.5, ease: 'power3' }), ry = gsap.quickTo(ring, 'y', { duration: 0.5, ease: 'power3' });
      var txt = $('span', ring);
      api.on(window, 'mousemove', function (e) { dx(e.clientX); dy(e.clientY); rx(e.clientX); ry(e.clientY); });
      api.on(doc, 'mouseover', function (e) {
        var tg = e.target.closest('[data-cursor]'), onBtn = e.target.closest('button,.kh-card__add');
        var mode = tg && !onBtn ? tg.getAttribute('data-cursor') : null;
        ring.classList.toggle('is-big', !!mode);
        if (mode && txt) txt.textContent = mode === 'drag' ? (ring.dataset.drag || 'Drag') : (ring.dataset.view || 'View');
      });
    }

    /* preloader */
    var pre = portals.filter(function (p) { return p.hasAttribute('data-kh-preloader'); })[0];
    if (pre && pre.isConnected) {
      pre.style.animation = 'none';
      if (lenis) lenis.stop();
      var num = $('[data-kh-pre-num]', pre), cnt = { v: 0 };
      gsap.timeline({ defaults: { ease: 'power4.out' }, onComplete: function () {
        pre.remove();
        try { sessionStorage.setItem('kh_pre', '1'); } catch (e) {}
        if (lenis) lenis.start();
      } })
        .to($$('.kh-pre__ring circle, .kh-pre__ring path', pre), { strokeDashoffset: 0, duration: 1.5, stagger: 0.15, ease: 'power2.inOut' })
        .from($$('.kh-pre__word span', pre), { yPercent: 110, duration: 1, stagger: 0.06 }, 0.3)
        .to(cnt, { v: 100, duration: 1.7, ease: 'power2.inOut', onUpdate: function () { if (num) num.textContent = Math.round(cnt.v); } }, 0)
        .to($$('.kh-pre__center, .kh-pre__count', pre), { opacity: 0, y: -20, duration: 0.5, ease: 'power2.in' }, '+=.15')
        .to($('.kh-pre__panel--top', pre), { yPercent: -100, duration: 1.1, ease: 'power4.inOut' }, '-=.1')
        .to($('.kh-pre__panel--bot', pre), { yPercent: 100, duration: 1.1, ease: 'power4.inOut' }, '<')
        .add(function () { introDone(); }, '-=.8');
    }
  };

  /* ----- hero slider ----- */
  C.hero = function (el, api) {
    var frame = $('[data-kh-hero-frame]', el);
    var slides = $$('.kh-hero__slide', el);
    if (!frame || !slides.length) return;
    var bars = $$('.kh-hero__bars i', el), barBtns = $$('.kh-hero__bars button', el), thumbs = $$('.kh-hero__thumb', el);
    var cur = $('[data-kh-hero-cur]', el), prevB = $('[data-kh-hero-prev]', el), nextB = $('[data-kh-hero-next]', el);
    var DUR = Math.max(2, +el.dataset.autoplay || 4);
    var i = 0, busy = false, prog, kb, held = false, timer;
    var label = function (n) { if (cur) cur.textContent = pad(n + 1); thumbs.forEach(function (t, k) { t.classList.toggle('is-on', k === n); }); };
    var swipe = function (go) {
      var sx = 0;
      api.on(frame, 'touchstart', function (e) { sx = e.touches[0].clientX; }, { passive: true });
      api.on(frame, 'touchend', function (e) { var dx = e.changedTouches[0].clientX - sx; if (Math.abs(dx) > 50) go(i + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1); });
    };
    var wire = function (go) {
      prevB && api.on(prevB, 'click', function () { go(i - 1, -1); });
      nextB && api.on(nextB, 'click', function () { go(i + 1, 1); });
      barBtns.forEach(function (b, n) { api.on(b, 'click', function () { go(n); }); });
      thumbs.forEach(function (b, n) { api.on(b, 'click', function () { go(n); }); });
      swipe(go);
      api.on(el, 'shopify:block:select', function (e) { var n = slides.indexOf(e.target); if (n < 0) return; held = true; go(n); if (prog) prog.pause(); clearInterval(timer); });
      api.on(el, 'shopify:block:deselect', function () { held = false; if (prog) prog.resume(); else if (slides.length > 1 && !G()) startTimer(); });
    };
    var startTimer = function () { clearInterval(timer); timer = setInterval(function () { if (!held) fallbackGo(i + 1); }, DUR * 1000); };
    var fallbackGo = function (n) {
      n = (n + slides.length) % slides.length;
      slides[i].classList.remove('is-active'); i = n; slides[i].classList.add('is-active'); label(i);
    };

    if (!G()) {
      if (slides.length > 1) { wire(function (n) { fallbackGo(n); if (!held) startTimer(); }); startTimer(); api.add(function () { clearInterval(timer); }); }
      return;
    }

    el.classList.add('is-ready');
    api.add(function () { el.classList.remove('is-ready'); if (prog) prog.kill(); if (kb) kb.kill(); });
    slides.forEach(function (s, n) { gsap.set(s, { visibility: n ? 'hidden' : 'visible', zIndex: n ? 0 : 1 }); });
    var content = function (s) { return $('.kh-hero__content', s); };
    var contentIn = function (s) {
      var c = content(s);
      if (!c) return;
      gsap.timeline()
        .fromTo(c, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' })
        .fromTo($$('.kh-hero__title .kh-mask > span', s), { yPercent: 110 }, { yPercent: 0, duration: 0.7, stagger: 0.06, ease: 'power4.out' }, '-=.5')
        .fromTo($$('[data-kh-hero-anim]', s), { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, stagger: 0.05, ease: 'power3.out' }, '-=.6');
    };
    var play = function () {
      bars.forEach(function (b, n) { gsap.set(b, { scaleX: n < i ? 1 : 0 }); });
      if (prog) prog.kill();
      if (slides.length > 1) {
        prog = gsap.fromTo(bars[i] || {}, { scaleX: 0 }, { scaleX: 1, duration: DUR, ease: 'none', onComplete: function () { go(i + 1, 1); } });
        if (held) prog.pause();
      }
      if (kb) kb.kill();
      var im = $('.kh-hero__img', slides[i]);
      if (im) kb = gsap.fromTo(im, { scale: 1.06 }, { scale: 1, duration: DUR + 1.6, ease: 'none' });
    };
    function go(n, dir) {
      if (busy) return;
      n = (n + slides.length) % slides.length;
      if (n === i) return;
      dir = dir || (n > i ? 1 : -1);
      busy = true;
      var a = slides[i], b = slides[n];
      gsap.set(b, { zIndex: 2, visibility: 'visible', clipPath: dir > 0 ? 'inset(0% 0% 0% 100%)' : 'inset(0% 100% 0% 0%)' });
      gsap.set(a, { zIndex: 1 });
      if (content(b)) gsap.set(content(b), { opacity: 0 });
      gsap.timeline({ onComplete: function () {
        a.classList.remove('is-active'); b.classList.add('is-active');
        gsap.set(a, { zIndex: 0, visibility: 'hidden', clearProps: 'clipPath' });
        gsap.set($('.kh-hero__media', a), { xPercent: 0 });
        busy = false;
      } })
        .to(content(a) || {}, { y: -30, opacity: 0, duration: 0.35, ease: 'power2.in' })
        .to(b, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.9, ease: 'power3.inOut' }, 0.1)
        .to($('.kh-hero__media', a), { xPercent: -18 * dir, duration: 0.9, ease: 'power3.inOut' }, 0.1)
        .fromTo($('.kh-hero__media', b), { xPercent: 18 * dir }, { xPercent: 0, duration: 0.9, ease: 'power3.inOut' }, 0.1)
        .add(function () { contentIn(b); }, 0.45);
      i = n; label(i); play();
    }
    wire(go);

    /* first slide is fully visible straight away (no reveal animation, nothing waits on fonts);
       only the autoplay timer waits for the preloader, so slide 1 still gets its full time */
    KH.intro.then(function () { if (el.isConnected) play(); });

    /* gentle depth as the hero scrolls away */
    gsap.to(frame, { scale: 0.94, ease: 'none', scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true } });
  };

  /* ----- infinite carousel / marquee ----- */
  C.loop = function (el, api) {
    var vp = $('.kh-loop__viewport', el), tr = $('.kh-loop__track', el);
    var set = tr && tr.firstElementChild;
    if (!vp || !set || !set.children.length) return;
    var bar = $('.kh-loop__bar i', el), prev = $('.kh-loop__prev', el), next = $('.kh-loop__next', el);
    var hint = $('[data-kh-hint]', el.closest('.kh') || el);
    var speed = +el.dataset.speed || 30;
    var dir = el.dataset.rev === 'true' ? -1 : 1;
    var hoverPause = el.dataset.hover !== 'false';
    var canDrag = el.dataset.drag !== 'false';
    var vel = el.dataset.velocity === 'true';

    /* make one set wider than the viewport, then add a twin for the seamless loop */
    var orig = Array.prototype.slice.call(set.children), guard = 0;
    while (set.getBoundingClientRect().width < vp.clientWidth * 1.1 && guard++ < 12) orig.forEach(function (c) { set.appendChild(quiet(c)); });
    var twin = quiet(set);
    tr.appendChild(twin);
    el.classList.add('is-looping');
    if (canDrag) el.classList.add('can-drag');
    if (hoverPause) el.classList.add('can-hover');
    api.add(function () { twin.remove(); $$('[data-kh-clone]', set).forEach(function (n) { n.remove(); }); el.classList.remove('is-looping', 'can-drag', 'can-hover', 'kh-loop--css', 'kh-loop--rev'); });

    var w = 1;
    var measure = function () { w = set.getBoundingClientRect().width || 1; if (!G()) el.style.setProperty('--kh-dur', (w / speed) + 's'); };
    measure();
    api.on(window, 'resize', measure);
    api.on(window, 'load', measure);
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(measure);

    if (!G()) {
      el.classList.add('kh-loop--css');
      if (dir < 0) el.classList.add('kh-loop--rev');
      return;
    }

    var base = 0, drag = 0, down = false, moved = false, sx = 0, lx = 0, lt = 0, v = 0;
    var off = { v: 0 }, st = { ts: 1, boost: 0, vdir: 1 };
    var setX = gsap.quickSetter(tr, 'x', 'px');
    var skTo = gsap.quickTo(tr, 'skewX', { duration: 0.6, ease: 'power3.out' });
    var wrap = function (x) { return (((x % w) + w) % w) - w; };
    var tick = function (time, dt) {
      if (!down) base -= dir * st.vdir * speed * (1 + st.boost) * Math.min(dt, 50) / 1000 * st.ts;
      var x = wrap(base + off.v + drag);
      setX(x);
      if (bar) bar.style.transform = 'scaleX(' + (-x / w).toFixed(4) + ')';
    };
    gsap.ticker.add(tick);
    api.add(function () { gsap.ticker.remove(tick); });

    if (vel) {
      var vdirOn = el.dataset.vdir === 'true';
      api.add(onVelocity(function (velo, d) {
        st.boost = Math.min(5, Math.abs(velo) / 280);
        if (vdirOn) st.vdir = d;
        gsap.to(st, { boost: 0, duration: 1.2, delay: 0.15, ease: 'power2.out', overwrite: 'auto' });
      }));
    }

    if (hoverPause) {
      var hov = false;
      var pause = function () { gsap.to(st, { ts: 0, duration: 0.8, ease: 'power2.out', overwrite: 'auto' }); if (hint) hint.classList.add('is-paused'); };
      var resume = function () { gsap.to(st, { ts: 1, duration: 1.2, ease: 'power2.in', overwrite: 'auto' }); if (hint) hint.classList.remove('is-paused'); };
      api.on(el, 'mousemove', function () { if (!hov) { hov = true; pause(); } });
      api.on(el, 'mouseleave', function () { if (hov) { hov = false; resume(); } });
      api.on(window, 'scroll', function () { if (hov) { hov = false; resume(); } }, { passive: true });
      api.on(el, 'focusin', pause);
      api.on(el, 'focusout', resume);
    }

    var step = function () { var c = set.firstElementChild; if (!c) return 300; return c.getBoundingClientRect().width + parseFloat(getComputedStyle(c).marginRight || 0); };
    if (next) api.on(next, 'click', function () { gsap.to(off, { v: off.v - step() * 2, duration: 1, ease: 'power3.inOut', overwrite: true }); });
    if (prev) api.on(prev, 'click', function () { gsap.to(off, { v: off.v + step() * 2, duration: 1, ease: 'power3.inOut', overwrite: true }); });

    if (canDrag) {
      api.on(vp, 'pointerdown', function (e) { if (e.button) return; down = true; moved = false; sx = lx = e.clientX; lt = performance.now(); v = 0; gsap.killTweensOf(off); });
      api.on(window, 'pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - sx;
        if (!moved && Math.abs(dx) > 6) { moved = true; el.classList.add('is-dragging'); }
        if (!moved) return;
        drag = dx;
        var now = performance.now(); v = (e.clientX - lx) / Math.max(1, now - lt); lx = e.clientX; lt = now;
        skTo(Math.max(-5, Math.min(5, -v * 2.5)));
      });
      var up = function () {
        if (!down) return;
        down = false; el.classList.remove('is-dragging');
        off.v += drag; drag = 0; skTo(0);
        if (moved) gsap.to(off, { v: off.v + v * 350, duration: 1.2, ease: 'power3.out' });
      };
      api.on(window, 'pointerup', up);
      api.on(window, 'pointercancel', up);
      api.on(vp, 'click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      api.on(vp, 'dragstart', function (e) { e.preventDefault(); });
      api.on(vp, 'wheel', function (e) { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) { e.preventDefault(); off.v -= e.deltaX; } }, { passive: false });
    }
  };

  /* ----- signature spotlight (pinned) ----- */
  C.spotlight = function (el, api) {
    var infos = $$('.kh-spot__i', el);
    if (!G() || !infos.length) return;
    el.classList.add('is-ready');
    api.add(function () { el.classList.remove('is-ready'); });
    var imgs = $$('.kh-spot__img', el), insets = $$('.kh-spot__inset', el), words = $$('.kh-spot__word', el);
    var idx = $$('.kh-spot__idx button', el), disc = $('.kh-spot__disc', el), num = $('[data-kh-spot-n]', el), line = $('.kh-spot__line i', el);
    var N = infos.length, stRef = null;
    var orbit = $('.kh-spot__orbit', el);
    if (orbit) gsap.to(orbit, { rotate: 360, duration: 40, repeat: -1, ease: 'none' });
    if (disc) gsap.set(disc, { backgroundColor: infos[0].dataset.tint || '#EAD8D2' });
    var mm = gsap.matchMedia();
    api.add(function () { mm.revert(); });
    mm.add('(min-width: 990px)', function () {
      if (N < 2) return;
      gsap.set(imgs.slice(1), { yPercent: 70, rotate: 14, scale: 0.8, autoAlpha: 0 });
      gsap.set(insets.slice(1), { autoAlpha: 0, y: 60, rotate: 6 });
      gsap.set(infos.slice(1), { autoAlpha: 0 });
      infos.slice(1).forEach(function (inf) { gsap.set($$('.kh-spot__a', inf), { yPercent: 110 }); });
      gsap.set(words.slice(1), { autoAlpha: 0, xPercent: 25 });
      var tl = gsap.timeline({ defaults: { ease: 'power2.inOut' } });
      tl.addLabel('s0');
      for (var k = 1; k < N; k++) {
        tl.to($$('.kh-spot__a', infos[k - 1]), { yPercent: -110, duration: 0.5, stagger: 0.04 })
          .to(imgs[k - 1], { yPercent: -60, rotate: -14, scale: 0.8, autoAlpha: 0, duration: 1 }, '<')
          .to(imgs[k], { yPercent: 0, rotate: 0, scale: 1, autoAlpha: 1, duration: 1 }, '<')
          .to(insets[k - 1] || {}, { autoAlpha: 0, y: -50, rotate: -6, duration: 0.6 }, '<')
          .to(insets[k] || {}, { autoAlpha: 1, y: 0, rotate: 0, duration: 0.7 }, '<.35')
          .to(words[k - 1] || {}, { autoAlpha: 0, xPercent: -25, duration: 1 }, '<-.35')
          .to(words[k] || {}, { autoAlpha: 1, xPercent: 0, duration: 1 }, '<')
          .to(disc, { backgroundColor: infos[k].dataset.tint || '#EAD8D2', duration: 1 }, '<')
          .to(disc, { scale: 1.05, duration: 0.5, yoyo: true, repeat: 1 }, '<')
          .set(infos[k - 1], { autoAlpha: 0 }, '<.4')
          .set(infos[k], { autoAlpha: 1 }, '<')
          .to($$('.kh-spot__a', infos[k]), { yPercent: 0, duration: 0.6, stagger: 0.05, ease: 'power3.out' }, '<')
          .addLabel('s' + k, '+=.15');
      }
      tl.to({}, { duration: 0.2 });
      var labels = Object.keys(tl.labels).map(function (l) { return tl.labels[l]; });
      var active = -1;
      stRef = ScrollTrigger.create({
        trigger: el, start: 'top top', end: function () { return '+=' + window.innerHeight * (N - 1) * 1.1; },
        pin: true, scrub: 1, animation: tl,
        snap: { snapTo: 'labels', duration: { min: 0.3, max: 0.8 }, delay: 0.12, ease: 'power2.inOut', inertia: false },
        onUpdate: function () {
          var tm = tl.time(), a = 0;
          labels.forEach(function (lt, n) { if (tm >= lt - 0.4) a = n; });
          if (a !== active) {
            active = a;
            idx.forEach(function (b, n) { b.classList.toggle('is-on', n === a); });
            if (num) num.textContent = pad(a + 1);
            if (line) gsap.to(line, { scaleY: (a + 1) / N, duration: 0.6, ease: 'power3.out' });
          }
        }
      });
      return function () { stRef = null; };
    });
    var toPiece = function (n) { if (stRef) KH.scrollTo(stRef.labelToScroll('s' + n) + 2); };
    idx.forEach(function (b, n) { api.on(b, 'click', function () { toPiece(n); }); });
    api.on(el, 'shopify:block:select', function (e) { var n = infos.indexOf(e.target); if (n > -1) toPiece(n); });
  };

  /* ----- product tabs ----- */
  C.tabs = function (el, api) {
    var tabs = $$('[role="tab"]', el), panels = $$('[role="tabpanel"]', el);
    if (!tabs.length) return;
    var show = function (n, focus) {
      tabs.forEach(function (t, k) { t.setAttribute('aria-selected', k === n ? 'true' : 'false'); t.tabIndex = k === n ? 0 : -1; });
      panels.forEach(function (p, k) { p.hidden = k !== n; });
      if (focus) tabs[n].focus();
      if (G() && panels[n]) { gsap.fromTo(panels[n].children, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.8, stagger: 0.06, ease: 'power3.out' }); refresh(); }
    };
    tabs.forEach(function (t, n) { api.on(t, 'click', function () { show(n); }); });
    api.on(el, 'keydown', function (e) {
      var n = tabs.indexOf(doc.activeElement);
      if (n < 0) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); show((n + 1) % tabs.length, true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); show((n - 1 + tabs.length) % tabs.length, true); }
    });
    api.on(el, 'shopify:block:select', function (e) { var n = tabs.indexOf(e.target); if (n > -1) show(n); });
  };

  /* ----- statement: words light up on scroll ----- */
  C.statement = function (el) {
    var p = $('[data-kh-words]', el);
    if (!p || !G()) return;
    if (!p.dataset.khSplitDone) {
      p.dataset.khSplitDone = '1';
      p.innerHTML = p.textContent.trim().split(/\s+/).map(function (w) { return '<span class="kh-w">' + esc(w) + '</span>'; }).join(' ');
    }
    gsap.fromTo($$('.kh-w', p), { opacity: 0.14 }, { opacity: 1, stagger: 0.1, ease: 'none', scrollTrigger: { trigger: p, start: 'top 80%', end: 'bottom 45%', scrub: true } });
  };

  /* ----- jewelry wall tilt ----- */
  C.wall = function (el) {
    var rows = $('.kh-wall__rows', el);
    if (!rows || !G()) return;
    var t = parseFloat(getComputedStyle(rows).getPropertyValue('--kh-tilt')) || -4;
    gsap.fromTo(rows, { rotate: t + 2, y: 60 }, { rotate: t - 2, y: -40, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true } });
  };

  /* ----- footer: accordions + back to top ----- */
  C.footer = function (el, api) {
    var cols = $$('.kh-ft__col', el);
    var mq = window.matchMedia('(max-width: 989px)');
    var sync = function () { cols.forEach(function (d) { d.open = !mq.matches; }); };
    sync();
    if (mq.addEventListener) { mq.addEventListener('change', sync); api.add(function () { mq.removeEventListener('change', sync); }); }
    cols.forEach(function (d) { var s = $('summary', d); if (s) api.on(s, 'click', function (e) { if (!mq.matches) e.preventDefault(); }); });

    var top = $('.kh-totop', el);
    if (top) {
      doc.body.appendChild(top);
      api.add(function () { top.remove(); });
      api.on(top, 'click', function () { KH.scrollTo(0); });
      var ring = $('.kh-totop__ring circle:last-child', top);
      var len = 169.6;
      var upd = function () {
        var max = doc.documentElement.scrollHeight - window.innerHeight;
        var p = max > 0 ? window.scrollY / max : 0;
        if (ring) ring.style.strokeDashoffset = len * (1 - p);
        top.classList.toggle('is-on', window.scrollY > window.innerHeight);
      };
      api.on(window, 'scroll', upd, { passive: true });
      upd();
    }
  };

  /* =====================================================================
     BOOT
     ===================================================================== */
  function boot() {
    if (G()) {
      gsap.registerPlugin(ScrollTrigger);
      ScrollTrigger.config({ ignoreMobileResize: true });
      initLenis();
      initVelocity();
    }
    var pre = $('[data-kh-preloader]');
    if (!pre || design || reduce || !G()) { if (pre) pre.remove(); introDone(); }
    initCart();
    mount(doc);
    html.classList.add('kh-ready');
    doc.addEventListener('shopify:section:load', function (e) { mount(e.target); });
    doc.addEventListener('shopify:section:unload', function (e) { unmount(e.target); });
    doc.addEventListener('shopify:section:reorder', refresh);
    window.addEventListener('load', refresh);
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(refresh);
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
